import {
  detectPersonalRecords,
  type CardioSegment,
  type DetectRecordsResult,
  type ExistingRecord,
  type LoggedSet,
} from '@liftx/core';
import { db, type LiftXDatabase } from '../db';
import { isLive } from '../tombstone';
import type { CardioSegmentRecord, PersonalRecordRecord } from '../types';

/** Same field-mapping gap as `sessionRepository.ts` — see its comment. */
function toCardioSegment(row: CardioSegmentRecord): CardioSegment {
  return {
    id: row.id,
    sessionExerciseId: row.sessionExerciseId,
    segmentIndex: row.orderIndex,
    durationS: row.durationS,
    distanceM: row.distanceM,
    calories: null,
    avgHr: row.avgHeartRate,
    maxHr: null,
    inclinePct: row.inclinePct ?? null,
    resistanceLevel: null,
    isCompleted: row.isCompleted,
  };
}

/**
 * Personal records: a rebuildable cache over logged sets, never a source of
 * truth (Phase 0 §5.3). Master Phase 6.
 *
 * All detection logic is `@liftx/core`'s `detectPersonalRecords` — this file
 * only persists its output: marking a superseded record `supersededAt`
 * rather than deleting it (so `isDominated` can still flag it for display),
 * and inserting the new one.
 */

export interface PersonalRecordsRepository {
  /** The current (non-superseded) record per kind/repCount, the baseline `detectPersonalRecords` compares against. */
  getExisting(exerciseId: string): Promise<ExistingRecord[]>;
  /** Persists a detection result: supersedes the old record for each achieved kind/repCount, inserts the new one. */
  applyDetected(sessionId: string, exerciseId: string, result: DetectRecordsResult): Promise<PersonalRecordRecord[]>;
  listCurrent(exerciseId: string): Promise<PersonalRecordRecord[]>;
  /**
   * Recomputes every exercise's records from scratch by replaying its logged
   * sets in session order. Built now (it falls out almost for free, once
   * `detectPersonalRecords` and real session data both exist) but
   * deliberately **not wired to any UI** — Settings-level "rebuild PRs" is
   * Phase 9's feature to expose, per the approved Phase 6 plan's Decision L.
   */
  rebuildAll(): Promise<number>;
  /**
   * The same replay, scoped to one exercise. Master Phase 7: used after a
   * history edit or a session delete, where only one exercise's records are
   * actually affected — recomputing the whole account for a single-set
   * correction would be needless work at any real history size.
   */
  rebuildForExercise(exerciseId: string): Promise<number>;
}

const keyOf = (kind: string, repCount: number | null): string => `${kind}#${repCount === null ? '-' : String(repCount)}`;

async function currentRows(database: LiftXDatabase, exerciseId: string): Promise<PersonalRecordRecord[]> {
  const rows = await database.personalRecords.where('exerciseId').equals(exerciseId).toArray();
  return rows.filter((row) => row.supersededAt === null);
}

async function persistResult(
  database: LiftXDatabase,
  sessionId: string,
  exerciseId: string,
  result: DetectRecordsResult,
  now: number,
): Promise<PersonalRecordRecord[]> {
  if (result.achieved.length === 0) return [];

  const existingRows = await currentRows(database, exerciseId);
  const existingByKey = new Map(existingRows.map((row) => [keyOf(row.kind, row.repCount), row]));

  const inserted: PersonalRecordRecord[] = [];
  for (const candidate of result.achieved) {
    const key = keyOf(candidate.kind, candidate.repCount);
    const superseded = existingByKey.get(key);
    if (superseded !== undefined) {
      await database.personalRecords.update(superseded.id, { supersededAt: now });
    }

    const record: PersonalRecordRecord = {
      id: crypto.randomUUID(),
      exerciseId,
      kind: candidate.kind,
      repCount: candidate.repCount,
      value: candidate.value,
      unit: candidate.unit,
      achievedAt: candidate.achievedAt,
      sessionId,
      setId: candidate.setId,
      segmentId: candidate.segmentId,
      supersededAt: null,
    };
    await database.personalRecords.add(record);
    inserted.push(record);
  }

  return inserted;
}

/**
 * Replays one exercise's records from scratch: deletes its existing rows
 * (scoped to this exercise only — never the whole table) and replays its
 * sessions in chronological order, exactly as `rebuildAll()` does per
 * exercise. A plain function, not transaction-wrapping itself, so callers
 * already inside a `database.transaction(...)` covering
 * `personalRecords`/`sessionExercises`/`sessionSets`/`cardioSegments`/`sessions`
 * can call it directly — `rebuildAll()`, `rebuildForExercise()`,
 * `integrityRepository.deleteSession()`, and `historyEditRepository.editSet()`
 * (Master Phase 7) all do.
 */
async function replayExercise(database: LiftXDatabase, exerciseId: string): Promise<number> {
  await database.personalRecords.where('exerciseId').equals(exerciseId).delete();

  const ownExercises = await database.sessionExercises.where('exerciseId').equals(exerciseId).filter(isLive).toArray();
  const withSession = await Promise.all(
    ownExercises.map(async (exercise) => ({
      exercise,
      session: await database.sessions.get(exercise.sessionId),
    })),
  );
  // Chronological replay: an earlier session's record must be set, and then
  // genuinely beaten, before a later one supersedes it — exactly what
  // `achievedAt` ordering in real use would produce.
  withSession.sort((a, b) => (a.session?.startedAt ?? 0) - (b.session?.startedAt ?? 0));

  let totalApplied = 0;
  for (const { exercise, session } of withSession) {
    // A deleted session's sets must never contribute to a rebuilt record —
    // this is the exact mechanism that makes deleting a session correctly
    // reduce a PR that session sourced, now that deletion is a tombstone
    // rather than an instant physical removal (`integrityRepository.ts`).
    if (session === undefined || !isLive(session)) continue;

    const sets = (await database.sessionSets
      .where('sessionExerciseId')
      .equals(exercise.id)
      .filter(isLive)
      .toArray()) as LoggedSet[];
    const segments = (
      await database.cardioSegments.where('sessionExerciseId').equals(exercise.id).filter(isLive).toArray()
    ).map(toCardioSegment);

    const existing = await currentRows(database, exerciseId).then((rows) =>
      rows.map((row) => ({ kind: row.kind, repCount: row.repCount, value: row.value })),
    );
    const result = detectPersonalRecords({
      exerciseId,
      sessionId: exercise.sessionId,
      modality: exercise.modality,
      sets,
      segments,
      existing,
      at: session.startedAt,
    });

    const applied = await persistResult(database, exercise.sessionId, exerciseId, result, session.startedAt);
    totalApplied += applied.length;
  }

  return totalApplied;
}

export { replayExercise };

export function createPersonalRecordsRepository(database: LiftXDatabase = db): PersonalRecordsRepository {
  const repository: PersonalRecordsRepository = {
    async getExisting(exerciseId): Promise<ExistingRecord[]> {
      const rows = await currentRows(database, exerciseId);
      return rows.map((row) => ({ kind: row.kind, repCount: row.repCount, value: row.value }));
    },

    async applyDetected(sessionId, exerciseId, result): Promise<PersonalRecordRecord[]> {
      return await database.transaction('rw', database.personalRecords, async () => {
        return await persistResult(database, sessionId, exerciseId, result, Date.now());
      });
    },

    async listCurrent(exerciseId): Promise<PersonalRecordRecord[]> {
      return await currentRows(database, exerciseId);
    },

    async rebuildAll(): Promise<number> {
      return await database.transaction(
        'rw',
        [database.personalRecords, database.sessionExercises, database.sessionSets, database.cardioSegments, database.sessions],
        async () => {
          const allExercises = await database.sessionExercises.filter(isLive).toArray();
          const fromSessions = allExercises.map((row) => row.exerciseId).filter((id): id is string => id !== null);
          // Also replay any exerciseId that currently HAS personalRecords rows
          // but no matching sessionExercises — a stale/orphaned record from
          // outside the normal write paths, which the old whole-table `.clear()`
          // used to wipe. Scoped replay must still clean these up (it will find
          // zero sessions and correctly leave zero rows) to preserve that.
          const fromExistingRecords = (await database.personalRecords.toArray()).map((row) => row.exerciseId);
          const exerciseIds = [...new Set([...fromSessions, ...fromExistingRecords])];

          let totalApplied = 0;
          for (const exerciseId of exerciseIds) {
            totalApplied += await replayExercise(database, exerciseId);
          }
          return totalApplied;
        },
      );
    },

    async rebuildForExercise(exerciseId): Promise<number> {
      return await database.transaction(
        'rw',
        [database.personalRecords, database.sessionExercises, database.sessionSets, database.cardioSegments, database.sessions],
        async () => await replayExercise(database, exerciseId),
      );
    },
  };

  return repository;
}

export const personalRecordsRepository = createPersonalRecordsRepository();
